import { z } from 'zod';
import { AssetFilterParams, AssetResponseDto, AssetType } from './asset-api.dto';
import { CaseResponseDto } from './cases-api.dto';

/**
 * Event types for real-time case updates
 */
export enum RealtimeEventType {
  CASE_CREATED = 'case.created',
  CASE_UPDATED = 'case.updated',
  CASE_CANCELLED = 'case.cancelled',
  CASE_DELETED = 'case.deleted',
  ASSET_STATUS_UPDATED = 'asset.statusUpdated',
  ASSET_LOCATION_UPDATED = 'asset.locationUpdated',
  ASSIGN_ASSET_DISCOVERED = 'assign.asset.discovered',
  PERITRACK_SCAN_STARTED = 'peritrack.scan.started',
  PERITRACK_SCAN_STOPPED = 'peritrack.scan.stopped',
  PERITRACK_SCAN_TERMINATED = 'peritrack.scan.terminated',
  RELEASE_ASSET_DISCOVERED = 'release.asset.discovered',
  RELEASE_ASSET_PROCESSED = 'release.asset.processed',
}

/**
 * Connection-status frames the SSE backend emits before the actual stream
 * (`{ connectionId, ... }` / `{ message, ... }`). Filter these out.
 */
export const RealtimeConnectionEventSchema = z
  .object({
    connectionId: z.unknown().optional(),
    message: z.unknown().optional(),
  })
  .refine((v) => v.connectionId !== undefined || v.message !== undefined);

/**
 * Minimal envelope every real-time event must satisfy. Per-variant `data`
 * shapes are intentionally validated as `unknown` here — downstream consumers
 * narrow on `type` and access the corresponding DTO themselves.
 */
export const RealtimeEventEnvelopeSchema = z
  .object({
    type: z.nativeEnum(RealtimeEventType),
    timestamp: z.union([z.string(), z.number()]),
    data: z.unknown(),
  })
  .passthrough();

/**
 * Filters for case events
 */
export interface CaseEventFilters {
  /** Start date for filtering cases (ISO string) */
  startDate?: string;
  /** End date for filtering cases (ISO string) */
  endDate?: string;
  /** Case status filter */
  status?: string;
}

/**
 * Filters for scan-driven asset discovery / processing events
 */
export interface AssetDiscoveryEventFilters {
  /** Scan identifier */
  scanId?: string;
  /** Hub device identifier */
  hubId?: string;
}

/**
 * Filters for asset update events
 */
export type AssetUpdateEventFilters = Pick<
  AssetFilterParams,
  'type' | 'name' | 'locationId' | 'status' | 'assetCatalogId'
>;

// Backward-compatible alias for older scan event filter usage.
export type AssetEventFilters = AssetDiscoveryEventFilters;

/**
 * Event subscription configuration
 */
export interface EventSubscription {
  /** Event type to subscribe to */
  eventType: RealtimeEventType;
  /** Optional filters for this event type */
  filters?:
    | CaseEventFilters
    | AssetDiscoveryEventFilters
    | AssetUpdateEventFilters;
}

/**
 * Subscription request body for SSE endpoint
 */
export interface RealtimeSubscriptionRequest {
  /** Array of event subscriptions */
  subscriptions: EventSubscription[];
}

/**
 * Base structure for real-time events
 */
export interface BaseRealtimeEvent {
  /** Event type */
  type: RealtimeEventType;
  /** Timestamp when the event occurred (ISO string) */
  timestamp: string;
}

/**
 * Case created event
 */
export interface CaseCreatedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.CASE_CREATED;
  /** Case data */
  data: {
    case: CaseResponseDto;
  };
}

/**
 * Case updated event
 */
export interface CaseUpdatedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.CASE_UPDATED;
  /** Case data */
  data: {
    case: CaseResponseDto;
  };
}

/**
 * Case cancelled event
 */
export interface CaseCancelledEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.CASE_CANCELLED;
  /** Case data */
  data: {
    case: CaseResponseDto;
  };
}

/**
 * Case deleted event
 */
export interface CaseDeletedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.CASE_DELETED;
  /** Case data */
  data: {
    case: CaseResponseDto;
  };
}

/**
 * Asset status updated event
 */
export interface AssetStatusUpdatedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.ASSET_STATUS_UPDATED;
  data: {
    asset: AssetResponseDto;
  };
}

/**
 * Asset location updated event
 */
export interface AssetLocationUpdatedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.ASSET_LOCATION_UPDATED;
  data: {
    asset: AssetResponseDto;
  };
}

export interface AssignAssetDiscoveredEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.ASSIGN_ASSET_DISCOVERED;
  data: {
    scanId: string;
    hubId: string;
    assetId: string;
    assetType: AssetType;
    assetName: string;
    tagId: string;
    discoveredAt: string;
    firstCartAssetId?: string | null;
  };
}

export enum PeritrackScanLifecycleReason {
  STARTED = 'started',
  STOP_REQUEST = 'stop-request',
  GATEWAY_DISCONNECT = 'gateway-disconnect',
  MAPPING_EXPIRED = 'mapping-expired',
}

export interface PeritrackScanLifecycleEventData {
  scanId: string;
  hubId: string;
  gatewayId: string;
  occurredAt: string;
  reason: PeritrackScanLifecycleReason;
}

export interface PeritrackScanStartedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.PERITRACK_SCAN_STARTED;
  data: PeritrackScanLifecycleEventData;
}

export interface PeritrackScanStoppedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.PERITRACK_SCAN_STOPPED;
  data: PeritrackScanLifecycleEventData;
}

export interface PeritrackScanTerminatedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.PERITRACK_SCAN_TERMINATED;
  data: PeritrackScanLifecycleEventData;
}

export interface ReleaseAssetDiscoveredEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.RELEASE_ASSET_DISCOVERED;
  data: {
    scanId: string;
    hubId: string;
    assetId: string;
    assetType: AssetType;
    assetName: string;
    tagId: string;
    discoveredAt: string;
  };
}

export enum ReleaseAssetProcessedOutcome {
  RELEASED = 'released',
  NOOP = 'noop',
  FAILED = 'failed',
}

export interface ReleaseAssetProcessedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.RELEASE_ASSET_PROCESSED;
  data: {
    scanId: string;
    hubId: string;
    assetId: string;
    assetType: AssetType;
    processedAt: string;
    outcome: ReleaseAssetProcessedOutcome;
    caseId?: string | null;
    reasonCode?: string | null;
    message?: string | null;
  };
}

export interface AssetLocationUpdatedEvent extends BaseRealtimeEvent {
  type: RealtimeEventType.ASSET_LOCATION_UPDATED;
  data: {
    asset: AssetResponseDto;
  }
}

/**
 * Discriminated union of all real-time events
 */
export type RealtimeEvent =
  | CaseCreatedEvent
  | CaseUpdatedEvent
  | CaseCancelledEvent
  | CaseDeletedEvent
  | AssetStatusUpdatedEvent
  | AssetLocationUpdatedEvent
  | AssignAssetDiscoveredEvent
  | PeritrackScanStartedEvent
  | PeritrackScanStoppedEvent
  | PeritrackScanTerminatedEvent
  | ReleaseAssetDiscoveredEvent
  | ReleaseAssetProcessedEvent
  | AssetLocationUpdatedEvent;